import { useCallback, useEffect } from 'react';
import { Icon } from './Primitives';

/**
 * Minimal lightbox for the lookbook: arrow/escape keys, click-outside to close,
 * and body scroll lock. Deliberately unstyled chrome so the photography carries it.
 */
export default function Lightbox({ photos, index, onClose, onIndex }) {
  const open = index !== null && index >= 0;
  const photo = open ? photos[index] : null;

  const next = useCallback(
    (step) => onIndex((index + step + photos.length) % photos.length),
    [index, photos.length, onIndex]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') next(1);
      if (e.key === 'ArrowLeft') next(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, next, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-royal-950/95 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-zoom-out"
      />

      <figure className="relative z-10 flex max-h-[92vh] max-w-[92vw] flex-col items-center gap-4">
        <img
          src={photo.src}
          alt={photo.alt}
          className="max-h-[80vh] w-auto object-contain shadow-lift ring-1 ring-gold-500/20"
        />
        <figcaption className="max-w-xl px-4 text-center text-[13px] leading-relaxed text-cream-200/70">
          {photo.alt}
          <span className="mt-1 block text-[11px] uppercase tracking-wide2 text-gold-400">
            {index + 1} / {photos.length}
          </span>
        </figcaption>
      </figure>

      {[
        ['Previous', -1, 'left-3 sm:left-6', 'rotate-180'],
        ['Next', 1, 'right-3 sm:right-6', ''],
      ].map(([label, step, pos, rot]) => (
        <button
          key={label}
          type="button"
          aria-label={label}
          onClick={() => next(step)}
          className={`absolute z-20 grid h-12 w-12 place-items-center border border-cream-50/20
            bg-royal-950/50 text-cream-100 transition-colors hover:border-gold-400
            hover:bg-gold-500 hover:text-royal-900 ${pos}`}
        >
          <Icon.Chevron className={`h-5 w-5 ${rot}`} />
        </button>
      ))}

      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-3 top-3 z-20 grid h-12 w-12 place-items-center border
          border-cream-50/20 bg-royal-950/50 text-cream-100 transition-colors
          hover:border-gold-400 hover:bg-gold-500 hover:text-royal-900 sm:right-6 sm:top-6"
      >
        <Icon.Close className="h-5 w-5" />
      </button>
    </div>
  );
}
